import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Car, Search, MapPin, Calendar as CalendarIcon, Star, 
  ChevronLeft, SlidersHorizontal, X, LayoutDashboard, AlertTriangle,
  BadgeCheck, Camera, ImageIcon, Fuel, Settings, Users, Zap, Anchor,
  Bike, Truck, Ship, Coffee
} from 'lucide-react';
import { format } from 'date-fns';
import { getVehiclePhotoUrl, filterValidPhotos, handleVehicleImageError } from '../lib/imageUtils';

// Category display config: label, icon, color, and optional contextChip.
// `contextChip` is a short secondary chip rendered below the category badge
// to give renters an instant "what kind of hire is this?" cue. Cars keep
// `contextChip: null` so the existing car card renders byte-for-byte unchanged.
const CATEGORY_CONFIG = {
  car: { label: 'Car', icon: Car, color: 'bg-blue-100 text-blue-700', contextChip: null },
  motorcycle: { label: 'Motorcycle', icon: Bike, color: 'bg-orange-100 text-orange-700', contextChip: 'Licence required' },
  boat: { label: 'Boat', icon: Ship, color: 'bg-cyan-100 text-cyan-700', contextChip: 'Engine-hours rental' },
  jetski: { label: 'Jet Ski', icon: Anchor, color: 'bg-teal-100 text-teal-700', contextChip: 'Licence required' },
  trailer: { label: 'Trailer', icon: Truck, color: 'bg-slate-200 text-slate-700', contextChip: 'No fuel / no km' },
  ebike: { label: 'eBike', icon: Zap, color: 'bg-green-100 text-green-700', contextChip: 'Battery-based usage' },
  electric_scooter: { label: 'Electric Scooter', icon: Zap, color: 'bg-violet-100 text-violet-700', contextChip: 'Battery-based usage' },
  mobility_scooter: { label: 'Mobility Scooter', icon: Zap, color: 'bg-rose-100 text-rose-700', contextChip: 'Battery-based usage' },
  specialty_asset: { label: 'Specialty Asset', icon: Coffee, color: 'bg-amber-100 text-amber-700', contextChip: 'Event hire' },
};

// Get 2-3 key specs to show on card, based on category
const getCardSpecs = (vehicle) => {
  const cs = vehicle.category_specs || {};
  const cat = vehicle.vehicle_category || 'car';
  const specs = [];

  switch (cat) {
    case 'car':
      if (cs.seats) specs.push(`${cs.seats} seats`);
      if (cs.transmission) specs.push(cs.transmission === 'automatic' ? 'Auto' : 'Manual');
      if (cs.fuel_type) specs.push(cs.fuel_type.charAt(0).toUpperCase() + cs.fuel_type.slice(1));
      break;
    case 'motorcycle':
      if (cs.engine_cc) specs.push(`${cs.engine_cc}cc`);
      if (cs.motorcycle_type) specs.push(cs.motorcycle_type.charAt(0).toUpperCase() + cs.motorcycle_type.slice(1));
      if (cs.licence_class_required) specs.push(`Licence: ${cs.licence_class_required}`);
      break;
    case 'boat':
      if (cs.hull_length) specs.push(`${cs.hull_length}m`);
      if (cs.passenger_capacity) specs.push(`${cs.passenger_capacity} pax`);
      if (cs.boat_type) specs.push(cs.boat_type.replace('_', ' '));
      break;
    case 'jetski':
      if (cs.engine_cc) specs.push(`${cs.engine_cc}cc`);
      if (cs.rider_capacity) specs.push(`${cs.rider_capacity} riders`);
      break;
    case 'trailer':
      if (cs.load_capacity) specs.push(`${cs.load_capacity}kg`);
      if (cs.trailer_type) specs.push(cs.trailer_type.replace('_', ' '));
      if (cs.braked_or_unbraked) specs.push(cs.braked_or_unbraked);
      break;
    case 'ebike':
    case 'electric_scooter':
      if (cs.battery_range_km) specs.push(`${cs.battery_range_km}km range`);
      if (cs.top_speed_kmh) specs.push(`${cs.top_speed_kmh}km/h`);
      break;
    case 'mobility_scooter':
      if (cs.battery_range_km) specs.push(`${cs.battery_range_km}km range`);
      if (cs.weight_capacity_kg) specs.push(`${cs.weight_capacity_kg}kg cap`);
      if (cs.foldable) specs.push('Foldable');
      if (cs.transport_friendly && specs.length < 3) specs.push('Transport OK');
      break;
    case 'specialty_asset': {
      const assetTypeLabel = {
        coffee_trailer: 'Coffee trailer',
        event_setup: 'Event setup',
        other: 'Specialty asset'
      }[cs.asset_type] || 'Specialty asset';
      specs.push(assetTypeLabel);
      if (cs.usage_type === 'delivered_by_host') specs.push('Delivered');
      else if (cs.usage_type === 'both') specs.push('Pickup or delivery');
      else if (cs.usage_type === 'pickup_by_renter') specs.push('Pickup');
      if (cs.event_only !== false) specs.push('Event use');
      break;
    }
    default:
      break;
  }
  return specs.slice(0, 3);
};

/**
 * Premium Vehicle Card Component
 * Clickable card with category badge + key specs
 */
const VehicleCard = ({ vehicle, onClick }) => {
  const validPhotos = filterValidPhotos(vehicle.photos);
  const photoCount = validPhotos?.length || 0;
  const cat = vehicle.vehicle_category || 'car';
  const catConfig = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.car;
  const cardSpecs = getCardSpecs(vehicle);
  
  return (
    <Card 
      onClick={onClick}
      className="group cursor-pointer bg-white border-0 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
      data-testid={`vehicle-card-${vehicle.vehicle_id}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      aria-label={`View ${vehicle.make} ${vehicle.model} - $${vehicle.daily_rate} per day`}
    >
      {/* Image Section */}
      <div className="aspect-[16/11] bg-slate-100 relative overflow-hidden">
        {validPhotos?.[0] ? (
          <img 
            src={getVehiclePhotoUrl(validPhotos[0])}
            alt={`${vehicle.make} ${vehicle.model}`}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
            onError={(e) => handleVehicleImageError(e, vehicle.vehicle_id)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            <catConfig.icon className="w-16 h-16 text-slate-300" />
          </div>
        )}
        
        {/* Category Badge - Top Left */}
        <div className="absolute top-3 left-3 flex flex-col items-start gap-1">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 backdrop-blur-sm text-xs font-semibold rounded-lg shadow-sm ${catConfig.color}`} data-testid="vehicle-category-badge">
            <catConfig.icon className="w-3 h-3" />
            {catConfig.label}
          </span>
          {cat === 'specialty_asset' && vehicle?.category_specs?.food_service_ready && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/95 text-white text-[10px] font-semibold rounded-md shadow-sm"
              data-testid="event-ready-badge"
            >
              Event Ready
            </span>
          )}
          {/* Category context chip — display-only, additive. Renders only
              when the category has a contextChip defined (everything except
              `car`). Mirrors the existing event-ready chip pattern so it
              stacks naturally below the category badge on mobile + desktop. */}
          {catConfig.contextChip && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/95 backdrop-blur-sm text-slate-700 text-[10px] font-semibold rounded-md shadow-sm border border-slate-200"
              data-testid="category-context-chip"
            >
              {catConfig.contextChip}
            </span>
          )}
        </div>
        
        {/* Photo Count - Bottom Right */}
        {photoCount > 1 && (
          <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs font-medium rounded-md">
            <ImageIcon className="w-3 h-3" />
            <span>{photoCount}</span>
          </div>
        )}
        
        {/* Availability Badge */}
        {vehicle.available_for_dates === false && (
          <div className="absolute top-3 right-3">
            <span className="px-2.5 py-1 bg-red-500 text-white text-xs font-medium rounded-lg">
              Unavailable
            </span>
          </div>
        )}
        
        {/* Rego Warning - Bottom Left */}
        {vehicle.rego_status?.warning_level === 'red' && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-md">
            <AlertTriangle className="w-3 h-3" />
            <span>Rego Expiring</span>
          </div>
        )}
        {vehicle.rego_status?.warning_level === 'yellow' && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded-md">
            <AlertTriangle className="w-3 h-3" />
            <span>Rego Soon</span>
          </div>
        )}
      </div>
      
      {/* Content Section */}
      <CardContent className="p-4">
        {/* Title Row */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-semibold text-[15px] text-slate-900 leading-tight line-clamp-1 group-hover:text-[#004B8D] transition-colors">
            {vehicle.make} {vehicle.model}
          </h3>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            <span className="text-sm font-medium text-slate-700">4.8</span>
          </div>
        </div>
        
        {/* Year */}
        <p className="text-[13px] text-slate-500 mb-1.5">{vehicle.year}</p>
        
        {/* Category Specs - Key differentiator */}
        {cardSpecs.length > 0 && (
          <p className="text-[12px] text-slate-500 mb-2 truncate" data-testid="vehicle-card-specs">
            {cardSpecs.join(' \u2022 ')}
          </p>
        )}
        
        {/* Location */}
        <div className="flex items-center text-[13px] text-slate-500 mb-3">
          <MapPin className="w-3.5 h-3.5 mr-1 text-slate-400 flex-shrink-0" />
          <span className="truncate">{vehicle.location}</span>
        </div>
        
        {/* Price & Trust Row */}
        <div className="flex items-end justify-between pt-2.5 border-t border-slate-100">
          {/* Price - Strong Visual Anchor */}
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold text-[#004B8D]">${vehicle.daily_rate}</span>
            <span className="text-sm text-slate-400 font-normal">/day</span>
          </div>
          
          {/* Trust Indicators - Subtle */}
          <div className="flex items-center gap-1.5">
            {vehicle.owner_verification_status === 'verified' && (
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50" title="Verified Host">
                <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" />
              </div>
            )}
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-50" title="Photo Evidence">
              <Camera className="w-3.5 h-3.5 text-blue-500" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const VehicleSearch = ({ defaultCategory = '', hideHeader = false }) => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    vehicle_type: searchParams.get('type') || '',
    vehicle_category: searchParams.get('category') || defaultCategory,
    min_price: searchParams.get('min') || '',
    max_price: searchParams.get('max') || '',
    start_date: searchParams.get('start') || '',
    end_date: searchParams.get('end') || ''
  });

  const [dateRange, setDateRange] = useState({
    from: filters.start_date ? new Date(filters.start_date) : undefined,
    to: filters.end_date ? new Date(filters.end_date) : undefined
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, []);

  // Auto-refetch when category filter changes (immediate feedback)
  useEffect(() => {
    fetchVehicles();
  }, [filters.vehicle_category]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.location) params.append('location', filters.location);
      if (filters.vehicle_type) params.append('vehicle_type', filters.vehicle_type);
      if (filters.vehicle_category) params.append('vehicle_category', filters.vehicle_category);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await api.get(`/vehicles?${params.toString()}`);
      setVehicles(response.data.vehicles || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const newFilters = {
      ...filters,
      start_date: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : '',
      end_date: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : ''
    };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    if (newFilters.location) params.set('location', newFilters.location);
    if (newFilters.vehicle_type) params.set('type', newFilters.vehicle_type);
    if (newFilters.vehicle_category) params.set('category', newFilters.vehicle_category);
    if (newFilters.min_price) params.set('min', newFilters.min_price);
    if (newFilters.max_price) params.set('max', newFilters.max_price);
    if (newFilters.start_date) params.set('start', newFilters.start_date);
    if (newFilters.end_date) params.set('end', newFilters.end_date);
    setSearchParams(params);
    
    fetchVehicles();
    setShowFilters(false);
  };

  // Handle category change: reset irrelevant filters and auto-refresh
  const handleCategoryChange = (value) => {
    const newCategory = value === "all_categories" ? "" : value;
    setFilters(prev => ({
      ...prev,
      vehicle_category: newCategory,
      // Reset vehicle_type when leaving 'car' category
      vehicle_type: newCategory === 'car' ? prev.vehicle_type : ''
    }));
  };

  const clearFilters = () => {
    setFilters({ location: '', vehicle_type: '', vehicle_category: '', min_price: '', max_price: '', start_date: '', end_date: '' });
    setDateRange({ from: undefined, to: undefined });
    setSearchParams({});
    fetchVehicles();
  };

  const vehicleTypes = [
    { value: 'sedan', label: 'Sedan' },
    { value: 'suv', label: 'SUV' },
    { value: 'ute', label: 'Ute' },
    { value: 'van', label: 'Van' },
    { value: 'luxury', label: 'Luxury' }
  ];

  const vehicleCategories = [
    { value: 'car', label: 'Cars' },
    { value: 'motorcycle', label: 'Motorcycles' },
    { value: 'boat', label: 'Boats' },
    { value: 'jetski', label: 'Jet Skis' },
    { value: 'trailer', label: 'Trailers' },
    { value: 'ebike', label: 'eBikes' },
    { value: 'electric_scooter', label: 'Electric Scooters' },
    { value: 'mobility_scooter', label: 'Mobility Scooters' },
    { value: 'specialty_asset', label: 'Specialty Assets' }
  ];

  // Determine which category-specific filters to show
  const selectedCat = filters.vehicle_category;
  const showVehicleType = !selectedCat || selectedCat === 'car';
  const showSeatsFilter = !selectedCat || selectedCat === 'car';

  // Get the appropriate dashboard route based on user role
  const getDashboardRoute = () => {
    if (!user) return '/';
    if (user.roles?.includes('platform_owner')) return '/platform-owner';
    if (user.roles?.includes('super_admin')) return '/super-admin';
    if (user.roles?.includes('admin')) return '/admin';
    if (user.roles?.includes('owner')) return '/host';
    return '/dashboard';
  };

  const handleVehicleClick = (vehicleId) => {
    navigate(`/vehicles/${vehicleId}`);
  };

  // Category-aware empty state message
  const getEmptyMessage = () => {
    if (selectedCat) {
      const catLabel = vehicleCategories.find(c => c.value === selectedCat)?.label || selectedCat;
      return `No ${catLabel.toLowerCase()} available yet`;
    }
    return 'No vehicles found';
  };

  const getEmptySubtext = () => {
    if (selectedCat) {
      return 'Try broadening your search or check back soon for new listings';
    }
    return 'Try adjusting your search filters';
  };

  // Active filter count for mobile badge
  const activeFilterCount = [
    filters.vehicle_category,
    filters.vehicle_type,
    filters.min_price,
    filters.max_price,
    filters.start_date
  ].filter(Boolean).length;

  return (
    <div className={hideHeader ? "bg-slate-50" : "min-h-screen bg-slate-50"}>
      {/* Header - Hidden when embedded in CategoryLandingPage */}
      {!hideHeader && (
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to={user ? getDashboardRoute() : "/"}>
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <Link to={user ? getDashboardRoute() : "/"} className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#004B8D] rounded-xl flex items-center justify-center">
                <Car className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="font-heading font-bold text-lg sm:text-xl text-[#004B8D] hidden xs:inline">HireHub</span>
            </Link>
          </div>
          {user && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(getDashboardRoute())}
              className="h-9 gap-2 text-sm"
              data-testid="goto-dashboard-btn"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </Button>
          )}
        </div>
      </header>
      )}

      {/* Search Bar - Polished */}
      <div className="bg-white border-b border-slate-100 py-4 sm:py-5">
        <div className="max-w-7xl mx-auto px-4">
          {/* Mobile Search Layout */}
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
            {/* Location Input */}
            <div className="flex-1 relative">
              <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Where do you want to pick up?"
                className="pl-10 h-11 sm:h-11 rounded-xl border-slate-200 focus:border-[#004B8D] focus:ring-[#004B8D]/20 text-sm"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                data-testid="location-input"
              />
            </div>

            {/* Date Picker & Actions Row */}
            <div className="flex gap-2">
              {/* Date Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex-1 sm:flex-none sm:min-w-[180px] h-11 px-3.5 rounded-xl justify-start text-left font-normal text-sm border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-slate-400 flex-shrink-0" />
                    <span className="truncate text-slate-600">
                      {dateRange.from ? (
                        dateRange.to ? (
                          <>{format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d")}</>
                        ) : (
                          format(dateRange.from, "MMM d")
                        )
                      ) : (
                        "Select dates"
                      )}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={1}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const compareDate = new Date(date);
                      compareDate.setHours(0, 0, 0, 0);
                      return compareDate < today;
                    }}
                    className="sm:hidden"
                  />
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={2}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const compareDate = new Date(date);
                      compareDate.setHours(0, 0, 0, 0);
                      return compareDate < today;
                    }}
                    className="hidden sm:block"
                  />
                </PopoverContent>
              </Popover>

              {/* Filter Button - Mobile Only */}
              <Button 
                variant="outline" 
                className="h-11 w-11 px-0 rounded-xl sm:hidden border-slate-200 hover:border-slate-300 hover:bg-slate-50 relative"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="filter-toggle-btn"
              >
                <SlidersHorizontal className="w-4 h-4 text-slate-500" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#004B8D] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              {/* Search Button */}
              <Button 
                onClick={handleSearch}
                className="bg-[#004B8D] hover:bg-[#003a6d] h-11 px-5 sm:px-6 rounded-xl font-medium shadow-sm"
                data-testid="search-btn"
              >
                <Search className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Search</span>
              </Button>
            </div>
          </div>

          {/* Extended Filters */}
          <div className={`mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3 ${showFilters ? 'block' : 'hidden sm:grid'}`}>
            <Select 
              value={filters.vehicle_category || "all_categories"} 
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger className="h-10 rounded-xl border-slate-200 text-sm" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_categories">All Categories</SelectItem>
                {vehicleCategories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {showVehicleType && (
              <Select 
                value={filters.vehicle_type || "all"} 
                onValueChange={(value) => setFilters({ ...filters, vehicle_type: value === "all" ? "" : value })}
              >
                <SelectTrigger className="h-10 rounded-xl border-slate-200 text-sm" data-testid="type-filter">
                  <SelectValue placeholder="Vehicle Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {vehicleTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Input
              type="number"
              placeholder="Min $"
              className="h-10 rounded-xl border-slate-200 text-sm"
              value={filters.min_price}
              onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
              data-testid="min-price-filter"
            />

            <Input
              type="number"
              placeholder="Max $"
              className="h-10 rounded-xl border-slate-200 text-sm"
              value={filters.max_price}
              onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
              data-testid="max-price-filter"
            />

            <Button 
              variant="ghost" 
              className="h-10 rounded-xl text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              onClick={() => {
                clearFilters();
                fetchVehicles();
              }}
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-1.5" />
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 py-5 sm:py-6">
        {/* Category filter chips — display-only enhancement reusing the
            existing handleCategoryChange + filters.vehicle_category state.
            Horizontal scroll on mobile, wraps naturally on desktop. Uses
            short chip-friendly labels; the dropdown keeps its full labels. */}
        <div className="mb-4 -mx-4 px-4 overflow-x-auto scrollbar-hide" data-testid="category-chip-row">
          <div className="flex items-center gap-2 min-w-max sm:flex-wrap sm:min-w-0">
            {(() => {
              const chipLabelOverride = {
                car: 'Cars',
                motorcycle: 'Motorcycles',
                boat: 'Boats',
                jetski: 'Jet Skis',
                trailer: 'Trailers',
                ebike: 'eBikes',
                electric_scooter: 'Scooters',
                mobility_scooter: 'Mobility',
                specialty_asset: 'Specialty',
              };
              return [{ value: '', label: 'All' }, ...vehicleCategories].map((c) => {
                const active = (c.value === '' && !selectedCat) || c.value === selectedCat;
                const label = c.value === '' ? c.label : (chipLabelOverride[c.value] || c.label);
                return (
                  <button
                    key={c.value || 'all'}
                    type="button"
                    onClick={() => handleCategoryChange(c.value === '' ? 'all_categories' : c.value)}
                    className={
                      'shrink-0 inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ' +
                      (active
                        ? 'bg-[#004B8D] text-white border-[#004B8D] shadow-sm'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50')
                    }
                    data-testid={`cat-chip-${c.value || 'all'}`}
                    aria-pressed={active}
                  >
                    {label}
                  </button>
                );
              });
            })()}
          </div>
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-5">
          <p className="text-slate-500 text-sm">
            {loading ? 'Searching...' : (
              <>
                <span className="font-medium text-slate-700">{total}</span>
                {selectedCat ? (
                  <> {vehicleCategories.find(c => c.value === selectedCat)?.label?.toLowerCase() || 'vehicles'} available</>
                ) : (
                  <> vehicles available</>
                )}
              </>
            )}
          </p>
          {selectedCat && !loading && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-slate-500 hover:text-slate-700 h-8 px-2"
              onClick={() => handleCategoryChange("all_categories")}
              data-testid="show-all-categories-btn"
            >
              Show all categories
            </Button>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-[#004B8D]"></div>
          </div>
        ) : vehicles.length === 0 ? (
          /* Empty State - Category-Aware */
          <div className="text-center py-20" data-testid="empty-state">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              {selectedCat && CATEGORY_CONFIG[selectedCat] ? (
                (() => {
                  const Icon = CATEGORY_CONFIG[selectedCat].icon;
                  return <Icon className="w-8 h-8 text-slate-400" />;
                })()
              ) : (
                <Car className="w-8 h-8 text-slate-400" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-testid="empty-state-title">{getEmptyMessage()}</h3>
            <p className="text-slate-500 text-sm mb-5">{getEmptySubtext()}</p>
            <Button 
              variant="outline"
              size="sm"
              className="rounded-xl"
              onClick={() => {
                clearFilters();
                fetchVehicles();
              }}
              data-testid="empty-state-clear-btn"
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          /* Vehicle Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {vehicles.map((vehicle) => (
              <VehicleCard 
                key={vehicle.vehicle_id}
                vehicle={vehicle}
                onClick={() => handleVehicleClick(vehicle.vehicle_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleSearch;
